import type { DocOption, DocOptionSet } from "building-types/options/optionSet";
import type { RWTable, Table } from "common/data/table";
import objectIsEmpty from "common/utils/objectIsEmpty";

/**
 * Overlays catalog values onto a doc option set. Options the catalog doesn't know keep their live
 * values. The only exception: the doc's name always wins for the set and its options.
 */
export function resolveCatalogDisplayOptionSet(
  docOptionSet: DocOptionSet,
  catalogOptionSet: DocOptionSet | undefined,
): DocOptionSet {
  if (!catalogOptionSet) {
    return docOptionSet;
  }
  const options: RWTable<DocOption> = {};
  for (const [optionId, liveOption] of Object.entries(docOptionSet.options)) {
    const catalogOption = catalogOptionSet.options[optionId];
    options[optionId] = catalogOption ? { ...catalogOption, name: liveOption.name } : liveOption;
  }
  return { ...catalogOptionSet, options, name: docOptionSet.name };
}

/**
 * Applies the overlay across a table of option sets so catalog values reach building generation
 * without mutating the doc. Returns the input reference untouched when nothing overlays, so
 * identity-memoized generation doesn't regenerate for non-catalog builders.
 */
export function overlayCatalogOntoOptionSets(
  optionSets: Table<DocOptionSet>,
  catalogOptionSets: Table<DocOptionSet>,
): Table<DocOptionSet> {
  if (objectIsEmpty(catalogOptionSets)) {
    return optionSets;
  }
  let result: RWTable<DocOptionSet> | undefined;
  for (const [optionSetId, optionSet] of Object.entries(optionSets)) {
    const catalogOptionSet = catalogOptionSets[optionSetId];
    if (!catalogOptionSet) {
      continue;
    }
    result ??= { ...optionSets };
    result[optionSetId] = resolveCatalogDisplayOptionSet(optionSet, catalogOptionSet);
  }
  return result ?? optionSets;
}
